import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Amazon book proof block — renders one or more real client books with
 * live rating + review count pulled from /api/amazon-book-stats, plus
 * "Buy on Amazon" and "See reviews" links.
 *
 * Books are rendered spine-out-friendly: portrait covers tilted slightly
 * for a physical bookshelf feel, arranged in a horizontal group.
 *
 * Props:
 *   books: [{ asin, title, subtitle, cover, seedRating, seedReviews,
 *             amazonUrl, format }]
 *   testId: string used to root the data-testid attribute tree
 */
export default function AmazonBookProof({ books = [], testId = 'amazon-book-proof' }) {
  const [stats, setStats] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        books.map(async (b) => {
          try {
            const r = await fetch(`${API}/api/amazon-book-stats?asin=${b.asin}`);
            if (!r.ok) return [b.asin, null];
            return [b.asin, await r.json()];
          } catch {
            return [b.asin, null];
          }
        })
      );
      if (!cancelled) {
        const next = {};
        for (const [asin, data] of entries) if (data) next[asin] = data;
        setStats(next);
      }
    })();
    return () => { cancelled = true; };
  }, [books]);

  if (!books.length) return null;

  return (
    <div className="mt-10" data-testid={testId}>
      <div className="grid gap-8 sm:grid-cols-2">
        {books.map((b, i) => {
          const live = stats[b.asin] || {};
          const rating = (typeof live.rating === 'number' ? live.rating : b.seedRating);
          const reviews = (typeof live.review_count === 'number' ? live.review_count : b.seedReviews);
          const tilt = i % 2 === 0 ? '-rotate-1' : 'rotate-1';
          return (
            <div
              key={b.asin}
              className="group relative bg-white rounded-[16px] border border-[#E2E8F0] p-5 sm:p-6 shadow-[0_18px_40px_-24px_rgba(30,42,68,0.35)] hover:shadow-[0_28px_60px_-24px_rgba(188,81,111,0.35)] transition-shadow"
              data-testid={`${testId}-book-${b.asin}`}
            >
              <div className="flex gap-4 sm:gap-5">
                <a
                  href={b.amazonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`relative flex-shrink-0 w-[110px] sm:w-[130px] block transform ${tilt} transition-transform duration-500 group-hover:rotate-0 group-hover:scale-[1.03]`}
                  aria-label={`Open ${b.title} on Amazon`}
                >
                  <img
                    src={b.cover}
                    alt={`${b.title} book cover, formatted and published on Amazon with Audio & Co.`}
                    title={`${b.title} | Published with Audio & Co.`}
                    width="260"
                    height="390"
                    loading="lazy"
                    className="w-full h-auto rounded-[6px] shadow-[0_16px_32px_-12px_rgba(30,42,68,0.55)] ring-1 ring-black/10"
                  />
                </a>
                <div className="min-w-0 flex flex-col">
                  <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-tight">
                    {b.title}
                  </h3>
                  {b.subtitle && (
                    <p className="text-[#475569] text-sm mt-0.5">{b.subtitle}</p>
                  )}

                  <div className="mt-3 flex items-center gap-2" aria-label={rating ? `${rating} out of 5 stars` : 'Rating loading'}>
                    <span className="flex" data-testid={`${testId}-stars-${b.asin}`}>
                      {[0,1,2,3,4].map((n) => (
                        <Star
                          key={n}
                          className="w-4 h-4"
                          strokeWidth={1.5}
                          fill={rating && n < Math.round(rating) ? '#F5B301' : 'transparent'}
                          color={rating && n < Math.round(rating) ? '#F5B301' : '#CBD5E1'}
                        />
                      ))}
                    </span>
                    <span className="text-[#1E2A44] font-semibold text-sm" data-testid={`${testId}-rating-${b.asin}`}>
                      {rating ? rating.toFixed(1) : '—'}
                    </span>
                    <span className="text-[#475569] text-sm" data-testid={`${testId}-reviews-${b.asin}`}>
                      ({reviews ? reviews.toLocaleString() : '—'} reviews)
                    </span>
                  </div>

                  {b.format && (
                    <p className="text-xs uppercase tracking-widest text-[#94A3B8] mt-2">{b.format}</p>
                  )}

                  <div className="mt-auto pt-4 flex flex-wrap gap-2">
                    <a
                      href={b.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#1E2A44] hover:bg-[#0F1B34] text-white text-sm font-semibold px-3.5 py-2 rounded-[8px] transition-colors"
                      data-testid={`${testId}-buy-${b.asin}`}
                    >
                      Buy on Amazon
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <a
                      href={`https://www.amazon.co.uk/product-reviews/${b.asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] hover:border-[#BC516F] text-[#1E2A44] text-sm font-semibold px-3.5 py-2 rounded-[8px] transition-colors"
                      data-testid={`${testId}-reviews-link-${b.asin}`}
                    >
                      See reviews
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
